import { OverlayDiv } from "@/components/OverlayDiv"
import { CurrencyPair } from "@/services/currencyPairs"
import { ExecutionStatus } from "@/services/executions"

import {
  onDismissMessage,
  TileState,
  TileStates,
  useTileState,
} from "../tile.state"
import { useTileCurrencyPair } from "../TileContext"
import { FailureOverlay, SuccessOverlay, WarningOverlay } from "./overlays"
import { PendingSpinner } from "./PendingSpinner"

export interface ExecutionOverlayProps {
  tileState: TileState
  currencyPair: CurrencyPair
  onClose: () => void
}

const ExecutionOverlay = ({
  tileState,
  currencyPair,
  onClose,
}: ExecutionOverlayProps) => {
  const isTooLong = tileState.status === TileStates.TooLong

  const isFailure =
    (tileState.status === TileStates.Finished &&
      tileState.trade.status === ExecutionStatus.Rejected) ||
    tileState.status === TileStates.CreditExceeded ||
    tileState.status === TileStates.Timeout

  const isSuccessful =
    tileState.status === TileStates.Finished &&
    tileState.trade.status === ExecutionStatus.Done

  return (
    <OverlayDiv>
      {(() => {
        if (isSuccessful) {
          return (
            <SuccessOverlay
              trade={tileState.trade}
              onClose={onClose}
              {...currencyPair}
            />
          )
        }
        if (isFailure) {
          return (
            <FailureOverlay
              tileState={tileState}
              onClose={onClose}
              {...currencyPair}
            />
          )
        }
        if (isTooLong) {
          return <WarningOverlay {...currencyPair} />
        }
      })()}
    </OverlayDiv>
  )
}

export const ExecutionResponse = () => {
  const currencyPair = useTileCurrencyPair()
  const tileState = useTileState(currencyPair.symbol)

  const props = {
    currencyPair,
    tileState,
    onClose: () => {
      onDismissMessage(currencyPair.symbol)
    },
  }

  return <ExecutionResponseInner {...props} />
}

export const ExecutionResponseInner = ({
  currencyPair,
  tileState,
  onClose,
}: ExecutionOverlayProps) => {
  if (tileState.status === TileStates.Ready) return null

  return (
    <div className="flex justify-center bg-primary">
      {tileState.status === TileStates.Started ? (
        <PendingSpinner />
      ) : (
        <ExecutionOverlay
          currencyPair={currencyPair}
          tileState={tileState}
          onClose={onClose}
        />
      )}
    </div>
  )
}
